import {
  Box,
  Button as MuiButton,
  ClickAwayListener,
  Typography,
} from '@mui/material'
import { colorPalette } from '../../../style/colorPalette'
import { WbsItemStatus } from '../../../../domain/entity/WbsItemEntity'
import ClearRoundedIcon from '@mui/icons-material/ClearRounded'
import { intl } from '../../../../i18n'
import { useCallback, useRef, useState } from 'react'
import { SelectOption } from '../../../model/selectOption'
import WbsItemSearchCheckboxSelectConditionPopper from '../../../pages/WbsItemSearch/components/Header/WbsItemSearchTypesCheckboxSelectCondition'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { Button } from '../../../components/buttons'
import { getWbsItemStatusDeepColorCode } from '../../../../lib/functions/wbsItem'
import { styled } from '@mui/material/styles'

const OptionIcon = styled('img')({
  width: '20px',
  height: '20px',
  objectFit: 'cover',
  borderRadius: '50%',
})

type SearchConditionSelectBoxCheckmarksProps = {
  value: string[] | undefined
  options: SelectOption[]
  title: string
  onChange: (newValue: string[] | string | undefined) => void
}

const SearchConditionSelectBoxCheckmarks = ({
  value,
  options,
  title,
  onChange,
}: SearchConditionSelectBoxCheckmarksProps) => {
  const [openPullDown, setOpenPullDown] = useState<boolean>(false)
  const [pullDownAnchorEl, setPullDownAnchorEl] = useState<Element>()
  const openPullDownRef = useRef<HTMLSpanElement>(null)
  const [openExpandIconPullDown, setOpenExpandIconPullDown] =
    useState<boolean>(false)
  const [expandIconAnchorEl, setExpandIconAnchorEl] = useState<Element>()
  const openExpandIconTypeRef = useRef<HTMLSpanElement>(null)

  const handlePullDownClose = () => {
    setPullDownAnchorEl(undefined)
    setOpenPullDown(false)
  }

  const handleExpandIconPullDownClose = () => {
    setExpandIconAnchorEl(undefined)
    setOpenExpandIconPullDown(false)
  }

  return (
    <Box
      sx={{
        border: `1px solid ${colorPalette.monotone[2]}`,
        borderRadius: '4px',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        padding: '5px 10px',
        height: '100%',
      }}
    >
      <Typography
        sx={{
          color: colorPalette.monotone[4],
          minWidth: '80px',
          marginRight: '10px',
        }}
      >
        {title}
      </Typography>
      <ClickAwayListener onClickAway={handlePullDownClose}>
        <span
          ref={openPullDownRef}
          style={{
            display: 'flex',
            flexDirection: 'row',
            maxWidth: '416px',
          }}
        >
          {value && value.length >= 1 ? (
            <SelectedConditionItem
              options={options}
              value={value}
              onChange={onChange}
            />
          ) : (
            <Button
              colorPattern="monotone"
              size="s"
              sx={{
                borderRadius: '15px',
                border: `1px dashed ${colorPalette.monotone[2]}`,
                background: colorPalette.monotone[0],
                color: colorPalette.monotone[3],
                margin: '4px auto 4px 15px',
                cursor: 'pointer',
              }}
              onClick={() => {
                if (openPullDown) {
                  setPullDownAnchorEl(undefined)
                  setOpenPullDown(false)
                } else {
                  setPullDownAnchorEl(openPullDownRef.current || undefined)
                  setOpenPullDown(true)
                }
              }}
            >
              <Typography>
                {intl.formatMessage({
                  id: 'wbsItemSearchFilterPanel.click.select',
                })}
              </Typography>
              <AddRoundedIcon sx={{ height: '12px', width: '12px' }} />
            </Button>
          )}
          {openPullDown ? (
            <WbsItemSearchCheckboxSelectConditionPopper
              open={openPullDown}
              anchorEl={pullDownAnchorEl}
              options={options}
              value={value}
              onChange={onChange}
              offset={20}
              placement={'bottom-start'}
            />
          ) : null}
        </span>
      </ClickAwayListener>
      <ClickAwayListener onClickAway={handleExpandIconPullDownClose}>
        <span
          ref={openExpandIconTypeRef}
          style={{
            display: 'flex',
            flexDirection: 'row',
            maxWidth: '450px',
            margin: 'auto 0 auto auto',
          }}
        >
          <MuiButton
            sx={{ minWidth: '20px', margin: '0 0 0 auto' }}
            onClick={() => {
              if (openExpandIconPullDown) {
                setExpandIconAnchorEl(undefined)
                setOpenExpandIconPullDown(false)
              } else {
                setExpandIconAnchorEl(
                  openExpandIconTypeRef.current || undefined
                )
                setOpenExpandIconPullDown(true)
              }
            }}
          >
            {openExpandIconPullDown ? (
              <ExpandLess sx={{ color: colorPalette.monotone[4] }} />
            ) : (
              <ExpandMore sx={{ color: colorPalette.monotone[4] }} />
            )}
          </MuiButton>
          {openExpandIconPullDown ? (
            <WbsItemSearchCheckboxSelectConditionPopper
              open={openExpandIconPullDown}
              anchorEl={expandIconAnchorEl}
              options={options}
              value={value}
              onChange={onChange}
              offset={18}
              placement={'bottom-end'}
            />
          ) : null}
        </span>
      </ClickAwayListener>
    </Box>
  )
}

type SelectedConditionProps = {
  options: SelectOption[]
  value: string[] | undefined
  onChange: (newValue: string[] | string | undefined) => void
}

const SelectedConditionItem = ({
  options,
  value,
  onChange,
}: SelectedConditionProps) => {
  const onChangeValue = useCallback(
    (v: any) => {
      const newValue = [...(value || [])]
      if (newValue.includes(v)) {
        newValue.splice((value || []).indexOf(v), 1)
      } else {
        newValue.push(v)
      }
      onChangeNewValue(newValue)
    },
    [value]
  )

  const onChangeNewValue = useCallback((newValueStr: string[] | undefined) => {
    if (!newValueStr) {
      onChange && onChange(undefined)
      return
    }

    onChange && onChange(newValueStr)
  }, [])

  const clear = useCallback(() => {
    if (onChange) {
      onChange('')
    }
  }, [])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
      }}
    >
      {options.map(option => (
        <Box
          key={option.value}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          {(value || []).some(v => v === option.value) ? (
            <>
              <Box
                sx={{
                  display: 'flex',
                  padding: '3px 5px',
                  borderRadius: '15px',
                  margin: '3px 5px',
                  flexWrap: 'wrap',
                  background:
                    getWbsItemStatusDeepColorCode(
                      option.value as WbsItemStatus
                    ) || colorPalette.monotone[1],
                }}
              >
                {option.iconUrl && <OptionIcon src={option.iconUrl} />}
                <Typography
                  key={option.value}
                  sx={{
                    margin: 'auto 5px',
                    color: colorPalette.monotone[5],
                  }}
                >
                  {option.name}
                </Typography>
                <Button
                  colorPattern="monotone"
                  sx={{
                    border: 'none',
                    background: colorPalette.monotone[0],
                    width: '10px',
                    height: '24px',
                    borderRadius: '15px',
                    cursor: 'pointer',
                  }}
                  onClick={() => onChangeValue(option.value)}
                >
                  <ClearRoundedIcon
                    sx={{
                      color: colorPalette.monotone[4],
                      height: '13px',
                      width: '13px',
                    }}
                  />
                </Button>
              </Box>
            </>
          ) : null}
        </Box>
      ))}
      <MuiButton
        sx={{ minWidth: '20px', margin: '3px auto 3px 0' }}
        onClick={clear}
      >
        <ClearRoundedIcon sx={{ color: colorPalette.monotone[4] }} />
      </MuiButton>
    </Box>
  )
}

export default SearchConditionSelectBoxCheckmarks
